import {
  PERMISSIONS,
  RESOURCES,
  type Grant,
  type Permission,
  type Resource,
  type Scope,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil, Plus, ShieldCheck, Trash2, Users as UsersIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Gate } from '../components/shell/gate.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { Input } from '../components/ui/input.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { describeError } from '../lib/api.ts';
import { api, type RoleView } from '../lib/client.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';

const RESOURCE_LABEL: Record<Resource, string> = {
  organisation: 'Organisation',
  user: 'Users',
  role: 'Roles',
  team: 'Teams',
  flag: 'Feature flags',
  candidate: 'Candidates',
  customer: 'Projects and deals',
  job: 'Jobs',
  document: 'Documents',
  task: 'Team tasks',
  field_definition: 'Custom fields',
  audit_log: 'Audit log',
  integration: 'API keys',
  activity: 'Activity ingest',
  message: 'Messages',
  automation: 'Automations',
  note_template: 'Note templates',
};

/** The matrix's columns: every action some resource has, in a reading order. */
const ACTIONS = [
  'read',
  'create',
  'update',
  'delete',
  'upload',
  'invite',
  'manage',
  'ingest',
] as const;
const has = (p: string): p is Permission => (PERMISSIONS as readonly string[]).includes(p);

const SCOPE_LABEL: Record<Scope, string> = { owned: 'Own', team: 'Team', all: 'All' };
const RANK: Record<Scope, number> = { owned: 1, team: 2, all: 3 };
const SCOPE_TONE: Record<Scope, string> = {
  all: 'bg-brand-soft text-brand-soft-foreground border-brand/30',
  team: 'bg-state-warning-bg text-state-warning-fg border-state-warning-border',
  owned: 'bg-muted text-foreground border-border',
};

type Matrix = Partial<Record<Permission, Scope>>;
const toMatrix = (grants: readonly Grant[]): Matrix =>
  Object.fromEntries(grants.map((g) => [g.permission, g.scope]));

interface Draft {
  key: string;
  name: string;
  description: string;
  inherits: string | null;
  grants: Matrix;
}

const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .slice(0, 63)
    .split('_')
    .filter(Boolean)
    .join('_')
    .replace(/^[^a-z]+/, '');

/**
 * Settings → Roles (TS-956): the system roles (fixed, shared by every organisation) and this
 * organisation's custom roles, each with its permission matrix — resource × action, and per
 * cell how far it reaches (own records, the team's, everything). A custom role may build on a
 * system role; what it inherits shows in the matrix and only wider grants are stored. A saved
 * change applies on the members' next request, no deploy.
 */
export function RolesPage() {
  const session = useSession();
  const qc = useQueryClient();
  const org = session.me.organisation.id;
  const canManage = session.can('role.manage');
  const roles = useQuery({ queryKey: ['roles', org], queryFn: api.roles.list });
  const [editing, setEditing] = useState<{ original: RoleView | null; draft: Draft } | null>(null);
  const [viewing, setViewing] = useState<RoleView | null>(null);
  const [deleting, setDeleting] = useState<RoleView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['roles', org] });

  const list = roles.data ?? [];
  const system = list.filter((r) => r.isSystem);
  const custom = list.filter((r) => !r.isSystem);
  const byKey = useMemo(() => new Map(list.map((r) => [r.key, r] as const)), [list]);

  const save = useMutation({
    mutationFn: async (e: { original: RoleView | null; draft: Draft }) => {
      const inherited = toMatrix(
        e.draft.inherits ? (byKey.get(e.draft.inherits)?.grants ?? []) : [],
      );
      // Store only what the parent does not already give at least as widely.
      const grants = (Object.entries(e.draft.grants) as Array<[Permission, Scope]>)
        .filter(([p, s]) => !inherited[p] || RANK[s] > RANK[inherited[p]])
        .map(([permission, scope]) => ({ permission, scope }));
      const body = {
        name: e.draft.name.trim(),
        description: e.draft.description.trim(),
        inherits: e.draft.inherits,
        grants,
      };
      return e.original
        ? api.roles.patch(e.original.key, body)
        : api.roles.create({ ...body, key: e.draft.key });
    },
    onSuccess: async () => {
      setEditing(null);
      setError(null);
      await refresh();
    },
    onError: (err) => setError(describeError(err)),
  });
  const remove = useMutation({
    mutationFn: (r: RoleView) => api.roles.remove(r.key),
    onSuccess: async () => {
      setDeleting(null);
      setError(null);
      await refresh();
    },
    onError: (err) => setError(describeError(err)),
  });

  return (
    <Gate permission="role.read">
      <PageHeader
        title="Roles"
        description="What each role can do, on which records. A change applies to everyone holding the role on their next click."
        actions={
          canManage && (
            <Button
              onClick={() =>
                setEditing({
                  original: null,
                  draft: { key: '', name: '', description: '', inherits: 'viewer', grants: {} },
                })
              }
              data-testid="new-role"
            >
              <Plus /> New role
            </Button>
          )
        }
      />
      {error && !editing && (
        <p
          className="bg-state-danger-bg text-state-danger-fg border-state-danger-border mb-5 rounded-xl border px-4 py-3 text-sm"
          role="alert"
        >
          {error}
        </p>
      )}
      {roles.isError && (
        <p className="text-danger text-sm" role="alert">
          {describeError(roles.error)}
        </p>
      )}

      <Section title="Custom roles" hint="This organisation's own roles.">
        {custom.length === 0 ? (
          <p className="text-muted-foreground border-border bg-card/50 rounded-card border border-dashed px-5 py-8 text-center text-sm">
            No custom roles yet.{' '}
            {canManage && 'Build one on top of a system role when none of them fits.'}
          </p>
        ) : (
          <RoleGrid>
            {custom.map((r) => (
              <RoleCard
                key={r.key}
                role={r}
                parent={r.inherits ? byKey.get(r.inherits) : undefined}
                actions={
                  canManage ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setEditing({
                            original: r,
                            draft: {
                              key: r.key,
                              name: r.name,
                              description: r.description,
                              inherits: r.inherits,
                              grants: toMatrix(r.grants),
                            },
                          })
                        }
                        aria-label={`Edit ${r.name}`}
                      >
                        <Pencil /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger hover:text-danger"
                        onClick={() => setDeleting(r)}
                        aria-label={`Delete ${r.name}`}
                      >
                        <Trash2 />
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setViewing(r)}>
                      Permissions
                    </Button>
                  )
                }
              />
            ))}
          </RoleGrid>
        )}
      </Section>

      <Section title="System roles" hint="The same in every organisation; they cannot be changed.">
        <RoleGrid>
          {system
            .filter((r) => r.key !== 'integration')
            .map((r) => (
              <RoleCard
                key={r.key}
                role={r}
                actions={
                  <Button size="sm" variant="outline" onClick={() => setViewing(r)}>
                    Permissions
                  </Button>
                }
              />
            ))}
        </RoleGrid>
      </Section>

      <Dialog open={viewing !== null} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{viewing?.name}</DialogTitle>
            <DialogDescription>{viewing?.description}</DialogDescription>
          </DialogHeader>
          {viewing && (
            <PermissionMatrix
              grants={toMatrix(viewing.grants)}
              inherited={toMatrix(
                viewing.inherits ? (byKey.get(viewing.inherits)?.grants ?? []) : [],
              )}
              inheritedFrom={viewing.inherits ? byKey.get(viewing.inherits)?.name : undefined}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-4xl">
          {editing && (
            <RoleEditor
              editing={editing}
              parents={system.filter((r) => r.key !== 'integration')}
              byKey={byKey}
              onChange={(draft) => setEditing({ ...editing, draft })}
              onCancel={() => setEditing(null)}
              onSave={() => save.mutate(editing)}
              saving={save.isPending}
              error={error}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>
              {deleting && deleting.memberCount > 0
                ? `${deleting.memberCount} member${deleting.memberCount === 1 ? ' holds' : 's hold'} it: move them to another role in Users first.`
                : 'Nobody holds it. This cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!deleting || deleting.memberCount > 0 || remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
              data-testid="confirm-delete-role"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Gate>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <div className="mb-4">
        <h3 className="text-muted-foreground text-xs font-bold tracking-[0.1em] uppercase">
          {title}
        </h3>
        <p className="text-muted-foreground mt-1 text-sm">{hint}</p>
      </div>
      {children}
    </section>
  );
}

function RoleGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{children}</div>;
}

function RoleCard({
  role,
  parent,
  actions,
}: {
  role: RoleView;
  parent?: RoleView | undefined;
  actions: React.ReactNode;
}) {
  return (
    <article
      className="bg-card border-border rounded-card shadow-card flex min-w-0 flex-col gap-4 border p-5"
      data-testid={`role-${role.key}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'grid size-10 shrink-0 place-items-center rounded-xl',
            role.isSystem
              ? 'bg-muted text-muted-foreground'
              : 'bg-brand-soft text-brand-soft-foreground',
          )}
        >
          {role.isSystem ? (
            <Lock className="size-[18px]" />
          ) : (
            <ShieldCheck className="size-[18px]" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold">{role.name}</p>
          <code className="text-muted-foreground text-xs">{role.key}</code>
        </div>
      </div>
      {role.description && (
        <p className="text-muted-foreground line-clamp-3 text-sm leading-relaxed">
          {role.description}
        </p>
      )}
      <div className="mt-auto flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">
            <UsersIcon className="size-3" aria-hidden /> {role.memberCount}
          </Badge>
          {parent && <Badge variant="outline">+ {parent.name}</Badge>}
          <Badge variant="outline">{role.grants.length} grants</Badge>
        </span>
        <span className="flex items-center gap-1">{actions}</span>
      </div>
    </article>
  );
}

function RoleEditor({
  editing,
  parents,
  byKey,
  onChange,
  onCancel,
  onSave,
  saving,
  error,
}: {
  editing: { original: RoleView | null; draft: Draft };
  parents: RoleView[];
  byKey: ReadonlyMap<string, RoleView>;
  onChange: (d: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}) {
  const d = editing.draft;
  const parent = d.inherits ? byKey.get(d.inherits) : undefined;
  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <DialogHeader>
        <DialogTitle>{editing.original ? `Edit ${editing.original.name}` : 'New role'}</DialogTitle>
        <DialogDescription>
          Start from a system role, then widen what this one needs. It can never grant more than you
          hold yourself.
        </DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Name
          <Input
            required
            aria-label="Role name"
            value={d.name}
            onChange={(e) =>
              onChange({
                ...d,
                name: e.target.value,
                ...(editing.original ? {} : { key: slug(e.target.value) }),
              })
            }
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Key
          <Input
            required
            aria-label="Role key"
            pattern="[a-z][a-z0-9_]{0,63}"
            disabled={editing.original !== null}
            value={d.key}
            onChange={(e) => onChange({ ...d, key: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium sm:col-span-2">
          Description
          <Input
            aria-label="Role description"
            value={d.description}
            onChange={(e) => onChange({ ...d, description: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Builds on
          <Select
            value={d.inherits ?? 'none'}
            onValueChange={(v) => onChange({ ...d, inherits: v === 'none' ? null : v })}
          >
            <SelectTrigger aria-label="Builds on">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nothing (start empty)</SelectItem>
              {parents.map((r) => (
                <SelectItem key={r.key} value={r.key}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>
      <PermissionMatrix
        grants={d.grants}
        inherited={toMatrix(parent?.grants ?? [])}
        inheritedFrom={parent?.name}
        onChange={(grants) => onChange({ ...d, grants })}
      />
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving || !d.name.trim() || !d.key} data-testid="save-role">
          Save role
        </Button>
      </DialogFooter>
    </form>
  );
}

/**
 * Resource × action, one cell per permission that exists. Read-only without `onChange`.
 * A cell shows the wider of the role's own scope and the inherited one; choosing nothing
 * (or less) leaves the inherited scope in force, which the cell says.
 */
export function PermissionMatrix({
  grants,
  inherited,
  inheritedFrom,
  onChange,
}: {
  grants: Matrix;
  inherited: Matrix;
  inheritedFrom?: string | undefined;
  onChange?: (next: Matrix) => void;
}) {
  const cols = ACTIONS.filter((a) => RESOURCES.some((r) => has(`${r}.${a}`)));
  return (
    <div
      className="border-border overflow-x-auto rounded-xl border"
      data-testid="permission-matrix"
    >
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-muted text-muted-foreground text-xs">
          <tr>
            <th className="px-3 py-2.5 text-left font-semibold">Resource</th>
            {cols.map((a) => (
              <th key={a} className="px-2 py-2.5 text-center font-semibold capitalize">
                {a}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {RESOURCES.map((r) => (
            <tr key={r} className="border-border border-t">
              <th scope="row" className="px-3 py-2 text-left font-medium whitespace-nowrap">
                {RESOURCE_LABEL[r]}
              </th>
              {cols.map((a) => {
                const p = `${r}.${a}`;
                if (!has(p)) return <td key={a} className="bg-muted/30" />;
                const own = grants[p];
                const from = inherited[p];
                const effective = own && (!from || RANK[own] >= RANK[from]) ? own : from;
                return (
                  <td key={a} className="px-1.5 py-1.5 text-center">
                    {onChange ? (
                      <select
                        aria-label={`${RESOURCE_LABEL[r]} ${a}`}
                        value={own ?? ''}
                        onChange={(e) => {
                          const next = { ...grants };
                          if (e.target.value) next[p] = e.target.value as Scope;
                          else delete next[p];
                          onChange(next);
                        }}
                        className={cn(
                          'focus-visible:ring-ring/40 h-8 w-full min-w-[86px] cursor-pointer rounded-lg border px-1.5 text-xs font-semibold focus-visible:ring-3 focus-visible:outline-none',
                          effective
                            ? SCOPE_TONE[effective]
                            : 'bg-card text-muted-foreground border-border',
                        )}
                      >
                        <option value="">{from ? `${SCOPE_LABEL[from]} ·inh.` : '—'}</option>
                        {(['owned', 'team', 'all'] as const).map((s) => (
                          <option key={s} value={s} disabled={!!from && RANK[s] <= RANK[from]}>
                            {SCOPE_LABEL[s]}
                          </option>
                        ))}
                      </select>
                    ) : effective ? (
                      <span
                        className={cn(
                          'inline-flex min-w-14 justify-center rounded-lg border px-2 py-1 text-xs font-semibold',
                          SCOPE_TONE[effective],
                        )}
                      >
                        {SCOPE_LABEL[effective]}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/60">—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-muted-foreground border-border border-t px-3 py-2 text-xs">
        Own = records they own · Team = their team&apos;s records · All = every record.
        {inheritedFrom && ` “·inh.” comes from ${inheritedFrom}; pick a wider scope to override.`}
      </p>
    </div>
  );
}
